import { getSupplierNameToDisplay } from './supplierCard';
import UspSource from '../Data/UspSource';
import SubmissionStatus from '../Data/SubmissionStatus';

/**
 * The first candidate carrying any USPs, else null. An empty list means absent
 * rather than a value: a brand with no USPs from client brands service for the
 * subcategory comes back as [], and must fall through instead of rendering a
 * card with nothing.
 */
const resolveUsps = (...candidates) =>
  candidates.find(({ usps }) => usps?.length > 0) ?? {
    source: UspSource.NONE,
    usps: null,
  };

/**
 * Resolution order of usps where the dynamic feature is enabled, i.e. client
 * brands service (the registry), then use the generic usps from the component
 * do not use the usps that are set on the capture component in its static state
 */
export const resolveDynamicUsps = ({ matchedUsps, genericUsps }) =>
  resolveUsps(
    { source: UspSource.CLIENT_BRANDS, usps: matchedUsps },
    { source: UspSource.COMPONENT_GENERIC_USPS, usps: genericUsps }
  );

/**
 * Resolution order for usps when the dynamic feature is not enabled - use the
 * usps set in the component, else fallback to use none. Do not use the generic
 * or usps from client brands as they are only available when the dynamic
 * feature is on.
 */
export const resolveStaticUsps = ({ componentUsps }) =>
  resolveUsps({ source: UspSource.COMPONENT_USPS, usps: componentUsps });

export const mapDynamicMatchToSupplier = ({
  match,
  fallbackSupplier,
  usps,
  uspsSource,
}) => ({
  supplier_id: match.supplierId,
  brandId: match.brandId,
  brandRevisionId: match.brandRevisionId,
  brandName: match.brandName || match.brandLabel,
  brandLogoUrl: match.brandLogoUrl,
  badge_label: fallbackSupplier?.badge_label || '',
  cta_text: match.ctaText || 'Request a Quote',
  cta_button_style: fallbackSupplier?.cta_button_style || 'primary',
  display_brand_name: true,
  selected_state: true,
  usps,
  uspsSource,
});

export const hasSecondaryDynamicMatches = ({
  dynamicSelectAndConnect,
  dynamicMatches,
}) => Boolean(dynamicSelectAndConnect) && (dynamicMatches?.length || 0) > 1;

const getPrimaryDynamicMatch = ({
  dynamicSelectAndConnect,
  dynamicMatches,
}) => {
  if (!dynamicSelectAndConnect) return null;
  if (!dynamicMatches?.length) return null;
  return dynamicMatches[0];
};

export const resolveSuppliers = ({
  dynamicSelectAndConnect,
  dynamicMatches,
  additionalSettings,
}) => {
  if (hasSecondaryDynamicMatches({ dynamicSelectAndConnect, dynamicMatches })) {
    const fallbackSupplier = additionalSettings?.suppliers?.[0];
    const genericUsps = additionalSettings?.generic_usps;
    // Every secondary is shown regardless of USP count; the primary (submitted)
    // match is rendered separately and is never included here.
    return dynamicMatches.slice(1).map((match) => {
      const { usps, source: uspsSource } = resolveDynamicUsps({
        matchedUsps: match.usps,
        genericUsps,
      });

      // Secondaries have no configured supplier to fall back on, so a card with
      // nothing to show renders bare rather than undefined.
      return mapDynamicMatchToSupplier({
        match,
        fallbackSupplier,
        usps: usps || [],
        uspsSource,
      });
    });
  }

  if (dynamicSelectAndConnect) return null;

  // Configured suppliers already carry their own USPs, so they are handed back
  // untouched - which also keeps the reference stable for the effects that
  // depend on it.
  return additionalSettings?.suppliers ?? null;
};

// Look up each selected supplier_id across both the static additionalSettings
// config and the gateway-returned dynamicMatches, returning { brandId,
// brandRevisionId } objects. selectOptionAndUpdateValue coerces ids to strings
// when toggled, so we key the lookup on strings too. Unmatched ids are skipped.
export const getSelectedSupplierBrands = ({
  selectAndConnectComponent,
  dynamicMatches,
}) => {
  if (!selectAndConnectComponent) return [];

  const lookup = new Map();

  const staticSuppliers =
    selectAndConnectComponent.component?.additionalSettings?.suppliers || [];
  staticSuppliers.forEach((supplier) => {
    lookup.set(String(supplier.supplier_id), {
      brandId: supplier.brandId,
      brandRevisionId: supplier.brandRevisionId,
    });
  });

  (dynamicMatches || []).forEach((match) => {
    lookup.set(String(match.supplierId), {
      brandId: match.brandId,
      brandRevisionId: match.brandRevisionId,
    });
  });

  const selectedIds = selectAndConnectComponent.input?.selectedOptions || [];
  return selectedIds
    .map((supplierId) => lookup.get(String(supplierId)))
    .filter(Boolean);
};

/**
 * The dynamic matches whose lead was actually sent: the primary (submitted
 * with the form) plus every secondary whose Select and Connect submission
 * completed, in display order. `selectAndConnectBrands` is the submission
 * ledger keyed by brandId; ids are compared as strings because option ids
 * are stringified on click.
 */
export const getSubmittedDynamicMatches = ({
  dynamicMatches,
  selectAndConnectBrands,
}) => {
  const [primary, ...secondaries] = dynamicMatches || [];
  if (!primary) return [];

  const completedBrandIds = new Set(
    Object.values(selectAndConnectBrands || {})
      .filter((brand) => brand.submissionStatus === SubmissionStatus.COMPLETE)
      .map((brand) => String(brand.brandId))
  );

  return [
    primary,
    ...secondaries.filter((match) =>
      completedBrandIds.has(String(match.brandId))
    ),
  ];
};

export const getSelectAndConnectMatchOptions = (selectAndConnectComponent) => {
  if (!selectAndConnectComponent) return [];

  const selectedIds = (
    selectAndConnectComponent.input?.selectedOptions || []
  ).map(String);

  const suppliers =
    selectAndConnectComponent.component?.additionalSettings?.suppliers || [];

  return suppliers
    .filter((supplier) => selectedIds.includes(String(supplier.supplier_id)))
    .map((supplier) => ({
      id: supplier.brandId || supplier.supplier_id,
      value: supplier.brandName,
      icon_url: supplier.brandLogoUrl,
    }));
};

export const dedupeOptionsById = (...optionArrays) => {
  const seen = new Set();
  const deduped = [];
  optionArrays.forEach((options) => {
    options.forEach((option) => {
      const key = String(option.id);
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(option);
    });
  });
  return deduped;
};

export const convertToShownMatch = (item) => ({
  supplierId: item.supplierId ?? item.supplier_id ?? null,
  brandId: item.brandId ?? null,
  brandRevisionId: item.brandRevisionId ?? null,
  brandName: item.brandName ?? item.brandLabel ?? null,
  matchLevel: item.matchLevel ?? null,
});

/**
 * Builds the selectAndConnectShown analytics payload: the submitted matches
 * (currently the single primary, empty in static mode) and the ordered list of
 * shown matches. The GTM translation layer maps `shown` onto GA4 ecommerce
 * items so each field lands in its own item-scoped parameter, keeping every
 * value under GA4's 100-character limit without serialising the whole list into
 * one param.
 */
export const buildSelectAndConnectShownMatches = ({
  dynamicSelectAndConnect,
  dynamicMatches,
  additionalSettings,
}) => {
  const isDynamic = Boolean(dynamicSelectAndConnect && dynamicMatches?.length);

  if (!isDynamic) {
    const suppliers = additionalSettings?.suppliers || [];
    return {
      submitted: [],
      shown: suppliers.map(convertToShownMatch),
    };
  }

  // Every match is shown regardless of USP count; the primary is also the
  // single submitted match.
  const [primary] = dynamicMatches;

  return {
    submitted: [convertToShownMatch(primary)],
    shown: dynamicMatches.map(convertToShownMatch),
  };
};

export const resolvePrimarySupplier = ({
  dynamicSelectAndConnect,
  dynamicMatches,
  additionalSettings,
  componentText,
  genericUsps,
}) => {
  const primaryDynamicMatch = getPrimaryDynamicMatch({
    dynamicSelectAndConnect,
    dynamicMatches,
  });

  const logoUrl =
    primaryDynamicMatch?.brandLogoUrl ?? additionalSettings?.brandLogoUrl;

  const supplierName = primaryDynamicMatch
    ? primaryDynamicMatch.brandName || primaryDynamicMatch.brandLabel
    : getSupplierNameToDisplay({
        brandLogoUrl: logoUrl,
        brandName: componentText,
        shouldDisplayBrandName: additionalSettings?.display_brand_name,
      });

  const { usps, source: uspsSource } = primaryDynamicMatch
    ? resolveDynamicUsps({ matchedUsps: primaryDynamicMatch.usps, genericUsps })
    : resolveStaticUsps({ componentUsps: additionalSettings?.usps });

  return {
    badgeText: additionalSettings?.badge_label,
    confirmationMessage: additionalSettings?.confirmation_message,
    ctaButtonStyle: additionalSettings?.cta_button_style,
    ctaLinkUrl: additionalSettings?.cta_link_url,
    ctaText: additionalSettings?.cta_text ?? primaryDynamicMatch?.ctaText,
    logoUrl,
    selectedState: additionalSettings?.selected_state,
    shouldForwardUrlParams:
      additionalSettings?.should_forward_all_url_parameters,
    supplierName,
    usps,
    uspsSource,
  };
};
